import { DT } from "../../constants.ts";
import { add, flatBasis, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyHeal, applyStatus, damageBarrier, isEnemy, lockOn, moveProjectile, playersInRadius, spawnProjectile, spawnZone } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { setForm } from "../../sim/sim.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { pk, zk, type HeroModule } from "../module.ts";
import { action, aura, commonInput, consume, groundAimPoint, ready, secs, startMove, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { ultDash: 0, veil: 1 } as const;
export const PK = { Dagger: pk(HeroId.CloakSwagger, 0), Terror: pk(HeroId.CloakSwagger, 1) } as const;
export const ZK = { Storm: zk(HeroId.CloakSwagger, 0), BondTrail: zk(HeroId.CloakSwagger, 1) } as const;

const def: HeroDef = {
  id: HeroId.CloakSwagger,
  slug: "cloak-and-swagger",
  name: "Cloak & Swagger",
  role: Role.Support,
  color: "#5b62d6",
  blurb: "Two-form strategist: Swagger heals with light daggers and veils; Cloak harries enemies with shadows and phases allies.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.72,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 4500,
  abilities: {
    [Slot.Primary]: { name: "Light Needle", key: "LMB", description: "Throw light daggers that heal allies or damage enemies.", cooldown: 0 },
    [Slot.Secondary]: { name: "Dagger Rain", key: "RMB", description: "Create a healing field at the target point.", cooldown: 12 },
    [Slot.Ability1]: { name: "Call Cloak", key: "Shift", description: "Switch to Cloak's shadow form.", cooldown: 0 },
    [Slot.Ability2]: { name: "Bright Veil", key: "E", description: "Send twin light veils that heal allies and cut foes.", cooldown: 10, charges: 2 },
    [Slot.Ultimate]: { name: "Forever Foxtrot", key: "Q", description: "Dash four times, leaving trails that heal allies and damage enemies.", cooldown: 0 },
  },
  passive: { name: "Shared Spotlight", description: "Swagger and Cloak share cooldowns, ultimate charge, and position when switching forms." },
  forms: [
    {
      name: "Cloak",
      color: "#4b2d8f",
      abilities: {
        [Slot.Primary]: { name: "Night Needle", key: "LMB", description: "Drain the enemy in front with shadow damage.", cooldown: 0 },
        [Slot.Secondary]: { name: "Dim Door", key: "RMB", description: "Phase and hasten nearby allies.", cooldown: 12 },
        [Slot.Ability1]: { name: "Call Swagger", key: "Shift", description: "Switch to Swagger's light form.", cooldown: 0 },
        [Slot.Ability2]: { name: "Fright Veil", key: "E", description: "Send a dark veil that wounds, slows, and marks an enemy.", cooldown: 10, charges: 2 },
      },
    },
  ],
};

const isCloak = (sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) => sim.c.Player.form[eid] === 1;

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.ultDash, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (c.Ult.active[eid]! > 0 && sim.tick % 10 === 0) spawnZone(sim, eid, ZK.BondTrail, sim.posOf(eid), 4, 1.2);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(11);
        setHv(c, eid, V.ultDash, 4);
        applyStatus(sim, eid, "unstoppable", 10, { source: eid });
        action(sim, eid, ActionKind.Ult, 30);
      }
      if (c.Ult.active[eid]! > 0 && pressed & Btn.Primary && hv(c, eid, V.ultDash) > 0) {
        setHv(c, eid, V.ultDash, hv(c, eid, V.ultDash) - 1);
        const d = wishDir(input);
        startMove(sim, eid, MoveKind.Dash, 14, 24, { x: d.x * 24, y: 0, z: d.z * 24 });
      }
      if (pressed & Btn.Ability1) {
        setForm(sim, eid, isCloak(sim, eid) ? 0 : 1, { hp: "frac" });
        action(sim, eid, ActionKind.Cast, 12);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.Cast, 16);
        if (isCloak(sim, eid)) {
          for (const { eid: a } of playersInRadius(sim, sim.posOf(eid), 10, (e) => !isEnemy(sim, eid, e), false)) {
            applyStatus(sim, a, "phased", 2, { source: eid });
            applyStatus(sim, a, "haste", 2, { source: eid, hastePct: 0.45 });
          }
        } else {
          const p = groundAimPoint(sim, eid, 24, 0.25);
          spawnZone(sim, eid, ZK.Storm, p, 5, 6);
        }
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 12);
        const a = aim(sim, eid);
        if (isCloak(sim, eid)) spawnProjectile(sim, eid, { kind: PK.Terror, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 20), radius: 0.18, seconds: 1.25, seq: input.seq });
        else {
          const { right } = flatBasis(input.yaw);
          for (const side of [-1, 1]) spawnProjectile(sim, eid, { kind: PK.Dagger, origin: add(a.origin, add(scale(a.dir, 0.5), scale(right, side * 0.2))), vel: scale(a.dir, 20), radius: 0.1, seconds: 1.25, seq: input.seq });
        }
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = isCloak(sim, eid) ? secs(0.1) : secs(1 / 2.27);
        action(sim, eid, isCloak(sim, eid) ? ActionKind.AltFire : ActionKind.Fire, 5);
        if (isCloak(sim, eid)) {
          const t = lockOn(sim, eid, 20, 0.2, false);
          if (t !== undefined) applyDamage(sim, t, 80 * DT, eid);
        } else {
          const a = aim(sim, eid);
          spawnProjectile(sim, eid, { kind: PK.Dagger, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 60), radius: 0.1, seconds: 1.2, seq: input.seq });
        }
      }
    },
  },
  projectiles: {
    [PK.Dagger]: {
      visual: { color: "#fff7ba", size: 0.11, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "both");
        if (hit) {
          if (hit.target !== undefined) {
            if (isEnemy(sim, owner, hit.target)) applyDamage(sim, hit.target, 18, owner, { pos: hit.pos });
            else {
              applyHeal(sim, owner, hit.target, 15);
              for (const { eid: a } of playersInRadius(sim, hit.pos, 8, (e) => !isEnemy(sim, owner, e), false)) applyHeal(sim, owner, a, 20, { silent: a !== hit.target });
            }
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 18, owner);
          sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
        } else if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.Terror]: {
      visual: { color: "#4b2d8f", size: 0.2, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "enemies");
        if (hit) {
          if (hit.target !== undefined) {
            applyDamage(sim, hit.target, 55, owner, { pos: hit.pos });
            applyStatus(sim, hit.target, "marked", 3, { source: owner, by: owner });
            applyStatus(sim, hit.target, "slow", 1.5, { source: owner, slowPct: 0.45 });
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 55, owner);
          sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Singularity });
          sim.destroyEntity(p);
        } else if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
  zones: {
    [ZK.Storm]: {
      visual: { color: "#fff7ba", dome: false },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        aura(sim, owner, sim.posOf(z), { radius: sim.c.Zone.radius[z]!, healAlliesPerSec: 50, los: false });
        if (sim.c.Zone.age[z] === 1) for (const { eid } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => !isEnemy(sim, owner, e), false)) applyHeal(sim, owner, eid, 60);
      },
    },
    [ZK.BondTrail]: {
      visual: { color: "#d8c7ff", dome: false },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        aura(sim, owner, sim.posOf(z), { radius: sim.c.Zone.radius[z]!, healAlliesPerSec: 250, damageEnemiesPerSec: 30, los: false });
      },
    },
  },
};

void addOverhealth;
export default module;
